'use client';

import { Button, DialogFooter, ScrollArea } from '@shared/components/shadcn';
import { CloseAccount, Verified } from '@shared/icons/system';
import { NumberFormats, ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { Link } from '../../../../Link/Link';
import type { CompleteStepProps } from '../types';

const CompleteStep = ({ result, value, onClose, questions, dataTrackContainer }: CompleteStepProps) => {
  const isPositiveResult = result === 'POSITIVE';
  const ResultIcon = isPositiveResult ? Verified : CloseAccount;

  const t = useTranslations('global_dialogs.affordability_questionnaire');
  const tForm = useTranslations('form');
  const currencyFormatter = useCurrencyFormatter();
  const { watch } = useFormContext();

  const statement = watch('statement');
  const occupation = watch('occupation');
  const industry = watch('industry');
  const pay = watch('pay');
  const spend = watch('spend');

  const statementValue = statement === 'question1_option1' ? 'Affordable' : 'Unaffordable';

  const resolveOptionLabel = (questionOrder: number, selectedOptionKey: string) => {
    const question = questions.find(question => question.order === questionOrder);
    const optionKey = question?.options.find(opt => opt === selectedOptionKey);
    return optionKey ? t(optionKey) : undefined;
  };

  const occupationValue = resolveOptionLabel(2, occupation);
  const industryValue = resolveOptionLabel(3, industry);
  const payValue = resolveOptionLabel(4, pay);
  const spendValue = resolveOptionLabel(5, spend);

  const linkRenderer = (chunks: React.ReactNode) => (
    <Link className="text-foreground-subtle" href={ROUTES.MY_ACCOUNT.UPLOAD_DOCUMENTS} target="_blank" rel="noopener" data-track-name="Contact Us">
      {chunks}
    </Link>
  );

  const formattedLimit = value ? currencyFormatter(value).format(NumberFormats.WITHOUT_DECIMALS).get() : '';

  return (
    <div
      className="flex min-h-0 flex-1 flex-col pt-10 md:pt-6"
      data-track-container={dataTrackContainer}
      data-track-statement={statementValue}
      data-track-occupation={occupationValue}
      data-track-industry={industryValue}
      data-track-pay={payValue}
      data-track-spend={spendValue}
    >
      <ScrollArea className="flex min-h-0 flex-1 flex-col gap-4">
        <ResultIcon size="extra-extra-large" className="text-accent-green" data-testid="affordability-questionnaire-success-icon" />
        <h4 className="mb-2 mt-3">{t('complete.heading')}</h4>

        {isPositiveResult ? (
          <div className="space-y-4 text-foreground-subtle" data-testid="complete-step-positive">
            <h6 className="paragraph-l-strong">{t('complete.positive.spend_limit_message', { limit: formattedLimit })}</h6>
            <p className="paragraph">{t('complete.positive.reset_info')}</p>
            <div className="space-y-2">
              <h6 className="paragraph-l-strong">{t('complete.positive.changing_limit_heading')}</h6>
              <p className="paragraph">{t.rich('complete.positive.contact_instruction', { link: linkRenderer })}</p>
              <ul className="list-disc list-inside space-y-1 paragraph">
                <li>{t('complete.positive.bullet_preferred_limit')}</li>
                <li>{t('complete.positive.bullet_payslip')}</li>
              </ul>
              <p className="paragraph">{t('complete.positive.limit_note')}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-foreground-subtle" data-testid="complete-step-concern">
            <h6 className="paragraph">{t('complete.concern.suspension_notice')}</h6>
            <p className="paragraph">{t('complete.concern.contact_message')}</p>
            <div className="space-y-2">
              <h6 className="paragraph">{t('complete.concern.what_happens_heading')}</h6>
              <p className="paragraph">{t.rich('complete.concern.contact_instruction', { link: linkRenderer })}</p>
              <p className="paragraph">{t('complete.concern.review_message')}</p>
            </div>
          </div>
        )}
      </ScrollArea>
      <DialogFooter data-track-container="Actions">
        <Button onClick={onClose} data-testid="done-button" data-track-name="Continue">
          {tForm('buttons.done')}
        </Button>
      </DialogFooter>
    </div>
  );
};

export { CompleteStep };
